import {
  EmbedOptions,
  EmbedProvider,
} from '../components/PdfViewer/index.types';

export interface NormalizedEmbedOptions {
  autoplay: boolean;
  mute: boolean;
  loop: boolean;
  controls: boolean;
}

// NOTE: autoplay는 음소거 상태에서만 브라우저가 허용하므로 mute를 강제한다
export const normalizeEmbedOptions = (
  options: EmbedOptions | null | undefined,
): NormalizedEmbedOptions => {
  const autoplay = options?.autoplay ?? false;

  return {
    autoplay,
    mute: autoplay ? true : options?.mute ?? false,
    loop: options?.loop ?? false,
    controls: options?.controls ?? true,
  };
};

interface GetEmbedIframeSrcParams {
  provider: EmbedProvider;
  videoId: string;
  options: NormalizedEmbedOptions;
}

export const getEmbedIframeSrc = ({
  provider,
  videoId,
  options,
}: GetEmbedIframeSrcParams) => {
  const params = new URLSearchParams();

  if (provider === 'youtube') {
    /* postMessage로 재생 상태를 수신하기 위해 필요하다 (로딩 커버 해제 타이밍) */
    params.set('enablejsapi', '1');
    if (options.autoplay) {
      params.set('autoplay', '1');
    }
    if (options.mute) {
      params.set('mute', '1');
    }
    if (options.loop) {
      // NOTE: youtube loop는 playlist 파라미터가 함께 있어야 동작한다
      params.set('loop', '1');
      params.set('playlist', videoId);
    }
    if (!options.controls) {
      params.set('controls', '0');
    }

    return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
  }

  if (options.autoplay) {
    params.set('autoplay', '1');
  }
  if (options.mute) {
    params.set('muted', '1');
  }
  if (options.loop) {
    params.set('loop', '1');
  }
  if (!options.controls) {
    params.set('controls', '0');
  }

  const query = params.toString();
  return `https://player.vimeo.com/video/${videoId}${query ? `?${query}` : ''}`;
};

const YOUTUBE_URL_REGEX =
  /^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})(?:[?&#].*)?$/;
const VIMEO_URL_REGEX =
  /^(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)(?:[?&#].*)?$/;

export const parseVideoUrl = (
  url: string,
): { provider: EmbedProvider; videoId: string } | null => {
  const trimmedUrl = url.trim();

  const youtubeMatch = trimmedUrl.match(YOUTUBE_URL_REGEX);
  if (youtubeMatch) {
    return { provider: 'youtube', videoId: youtubeMatch[1] };
  }

  const vimeoMatch = trimmedUrl.match(VIMEO_URL_REGEX);
  if (vimeoMatch) {
    return { provider: 'vimeo', videoId: vimeoMatch[1] };
  }

  return null;
};
